import { Suspense } from "react";
import {
  FluentProvider,
  Spinner,
  webLightTheme,
} from "@fluentui/react-components";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { setApiConfig, getDeviceUDID, clearTokens } from "@packages/api";
import { queryClient } from "./api/queryClient";
import App from "./App";
import "./i18n";

const params = new URLSearchParams(window.location.search);

// localStorage is origin-scoped, so cached tokens leak across users who
// share the same browser (e.g. two Dynamics users in the same workstation).
// The host passes email_hint on the iframe URL; if it changed since last
// mount, wipe the cached session so the next call re-exchanges.
const EMAIL_HINT_KEY = "_xm_email_hint";
const hint = params.get("email_hint") ?? "";
const lastHint = localStorage.getItem(EMAIL_HINT_KEY);
if (hint && lastHint && hint !== lastHint) {
  clearTokens();
}
if (hint) localStorage.setItem(EMAIL_HINT_KEY, hint);

setApiConfig({
  serverUrl:
    params.get("serverUrl") ||
    import.meta.env.VITE_SERVER_URL ||
    "https://server-v6.xmreality.com",
  apiVersion: "v17",
  deviceUdid: params.get("deviceId") || getDeviceUDID(),
  clientVersion: import.meta.env.VITE_VERSION || "1.0.0-dev",
  clientPlatform: "guide-station",
});

const root = createRoot(document.getElementById("root")!);

// Show centered spinner while waiting for token
root.render(
  <FluentProvider theme={webLightTheme}>
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <Spinner />
    </div>
  </FluentProvider>,
);

function renderApp(callId: number) {
  root.render(
    <Suspense fallback={<Spinner />}>
      <QueryClientProvider client={queryClient}>
        <FluentProvider theme={webLightTheme}>
          <App callId={callId} />
        </FluentProvider>
      </QueryClientProvider>
    </Suspense>,
  );
}

const callId = Number(params.get("callId") || 0);

renderApp(callId);
